/**
 * Spacing, radius and control-size tokens (DIS-1844).
 *
 * Source: Figma "Loyalty Sign-Up Redesign" (file JNExbbbe4KI568IdRlm8Rh), Lite sign-up
 * entry frames 160:8315 (mobile) and 536:4247 (desktop). The mobile frame stacks
 * `spacer-mob` instances of 16, 24, 40 and 64. The desktop card uses 40 and 48. The
 * field and CTA internals add 2, 4, 6, 8 and 12. Together they form `space`.
 *
 * Additive: no existing literal was migrated onto these tokens.
 */

const freeze = <T extends object>(o: T): Readonly<T> => Object.freeze(o);

export const space = freeze({
  s2: 2,
  s4: 4,
  s6: 6,
  s8: 8,
  s12: 12,
  s16: 16,
  s24: 24,
  s32: 32,
  s40: 40,
  s48: 48,
  s64: 64,
} as const);

export const radius = freeze({
  /** Input and CTA corners. */
  control: 4,
  /** Desktop sign-up card corners. */
  card: 8,
} as const);

export const control = freeze({
  /**
   * Painted field height. Figma draws the input 40 tall (DIS-1855).
   * The visibility control is this tall inside the box, and grows its tap
   * target to `inputHeight` with hitSlop so the control stays at least 44×44.
   */
  fieldHeight: 40,
  /** Minimum tap target. Not the painted height. */
  inputHeight: 44,
  ctaHeight: 42,
  borderWidth: 1,
} as const);

/** Form-field internals. Same on every width. */
export const field = freeze({
  labelGap: space.s6,
  requiredMarkGap: space.s2,
  paddingX: space.s12,
} as const);

/** Desktop sign-up card (536:4504). There is no mobile card. */
export const card = freeze({
  width: 636,
  paddingY: space.s48,
  paddingX: space.s64,
  /** Field and CTA column inside the card. */
  formWidth: 343,
} as const);

/**
 * Desktop OTP row (536:4581, inside card 536:4563): six 49 x 52 boxes, 8 apart,
 * so the row is 334 wide and centred in the 343 form column. Opt-in through
 * Otp's `boxSize`; every other Otp keeps its flex-grown boxes.
 */
export const otp = freeze({
  boxWidth: 49,
  boxHeight: 52,
  gap: space.s8,
} as const);

const mobile = freeze({
  gutter: space.s16,
  headerToContent: space.s32,
  /** Heading → body. */
  stackRelated: space.s16,
  /** Banner → heading, field → CTA. */
  stackGroup: space.s24,
  /** Intro → form. */
  stackSection: space.s40,
  pageEnd: space.s64,
});

const desktop = freeze({
  gutter: space.s16,
  // Assumption: 536:4247 is a mock pasted over a bitmap, so its 35px header offset
  // is not a spec. 40 repeats the measured banner → card gap.
  headerToContent: space.s40,
  stackRelated: space.s16,
  stackGroup: space.s40,
  stackSection: space.s40,
  pageEnd: space.s64,
});

/**
 * Width-dependent page rhythm. Pick with `isDesktopCard` from `useWideLayout()` in
 * app/screens/account/signUp/SignUpPageLayout.tsx:
 * `const rhythm = isDesktopCard ? layout.desktop : layout.mobile;`
 */
export const layout = freeze({mobile, desktop});
